/**
 * MarkdownEditor Component
 *
 * CodeMirror-based markdown editor with syntax highlighting.
 */
import { useCallback, useRef, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import CodeMirror, { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { languages } from '@codemirror/language-data';
import { EditorView } from '@codemirror/view';
import { useEditorStore } from '../../stores/editorStore';

interface MarkdownEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  minHeight?: string;
  maxHeight?: string;
  className?: string;
  autoFocus?: boolean;
}

// Custom theme for the editor
const customTheme = EditorView.theme({
  '&': {
    fontSize: '14px',
    fontFamily: 'ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace',
  },
  '.cm-content': {
    padding: '12px 0',
  },
  '.cm-line': {
    padding: '0 16px',
  },
  '&.cm-focused': {
    outline: 'none',
  },
  '.cm-gutters': {
    backgroundColor: '#f9fafb',
    borderRight: '1px solid #e5e7eb',
  },
  '.cm-activeLineGutter': {
    backgroundColor: '#f3f4f6',
  },
  '.cm-activeLine': {
    backgroundColor: '#f9fafb',
  },
  '.cm-selectionBackground': {
    backgroundColor: '#dbeafe !important',
  },
  '&.cm-focused .cm-selectionBackground': {
    backgroundColor: '#bfdbfe !important',
  },
  '.cm-cursor': {
    borderLeftColor: '#3b82f6',
    borderLeftWidth: '2px',
  },
});

const MarkdownEditor = forwardRef<ReactCodeMirrorRef, MarkdownEditorProps>(({
  value,
  onChange,
  placeholder = 'Scrivi qui il contenuto dell\'offerta in Markdown...',
  readOnly = false,
  minHeight = '400px',
  maxHeight = '600px',
  className = '',
  autoFocus = false,
}, ref) => {
  const editorRef = useRef<ReactCodeMirrorRef>(null);

  // Forward the ref
  useImperativeHandle(ref, () => editorRef.current as ReactCodeMirrorRef);

  // Editor store - use selectors to prevent unnecessary re-renders
  const content = useEditorStore((state) => state.content);
  const setContent = useEditorStore((state) => state.setContent);
  const setCursorPosition = useEditorStore((state) => state.setCursorPosition);
  const setSelection = useEditorStore((state) => state.setSelection);
  const setSelectedText = useEditorStore((state) => state.setSelectedText);

  // Use controlled value if provided, otherwise use store
  const editorValue = value !== undefined ? value : content;

  // Handle content changes
  const handleChange = useCallback((val: string) => {
    if (onChange) {
      onChange(val);
    } else {
      setContent(val);
    }
  }, [onChange, setContent]);

  // Handle cursor/selection changes - use ref to avoid recreating the handler
  const handleSelectionChangeRef = useRef((viewUpdate: any) => {
    const { state } = viewUpdate;
    const selection = state.selection.main;

    setCursorPosition(selection.head);

    if (selection.from !== selection.to) {
      setSelection({ from: selection.from, to: selection.to });
      setSelectedText(state.sliceDoc(selection.from, selection.to));
    } else {
      setSelection(null);
      setSelectedText('');
    }
  });

  // Keep the ref updated with latest store functions
  useEffect(() => {
    handleSelectionChangeRef.current = (viewUpdate: any) => {
      const { state } = viewUpdate;
      const selection = state.selection.main;

      setCursorPosition(selection.head);

      if (selection.from !== selection.to) {
        setSelection({ from: selection.from, to: selection.to });
        setSelectedText(state.sliceDoc(selection.from, selection.to));
      } else {
        setSelection(null);
        setSelectedText('');
      }
    };
  }, [setCursorPosition, setSelection, setSelectedText]);

  // Stable callback that uses the ref
  const handleSelectionChange = useCallback((viewUpdate: any) => {
    handleSelectionChangeRef.current(viewUpdate);
  }, []);

  // Auto focus
  useEffect(() => {
    if (autoFocus && editorRef.current?.view) {
      editorRef.current.view.focus();
    }
  }, [autoFocus]);

  // Extensions - memoized to prevent infinite re-renders
  // handleSelectionChange is stable (empty deps) so extensions never need to change
  const extensions = useMemo(() => [
    markdown({
      base: markdownLanguage,
      codeLanguages: languages,
    }),
    customTheme,
    EditorView.lineWrapping,
    EditorView.updateListener.of(handleSelectionChange),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <div className={`border border-gray-300 rounded-lg overflow-hidden ${className}`}>
      <CodeMirror
        ref={editorRef}
        value={editorValue}
        onChange={handleChange}
        extensions={extensions}
        placeholder={placeholder}
        readOnly={readOnly}
        basicSetup={{
          lineNumbers: true,
          highlightActiveLineGutter: true,
          highlightActiveLine: true,
          foldGutter: true,
          dropCursor: true,
          allowMultipleSelections: false,
          indentOnInput: true,
          bracketMatching: true,
          closeBrackets: true,
          autocompletion: true,
          rectangularSelection: true,
          crosshairCursor: false,
          highlightSelectionMatches: true,
          searchKeymap: true,
          history: true,
          drawSelection: true,
        }}
        style={{
          minHeight,
          maxHeight,
          overflow: 'auto',
        }}
      />
    </div>
  );
});

MarkdownEditor.displayName = 'MarkdownEditor';

export default MarkdownEditor;

// Export utility functions for parent components
export function useEditorRef() {
  const editorRef = useRef<ReactCodeMirrorRef>(null);

  const insertText = useCallback((text: string, position?: number) => {
    const view = editorRef.current?.view;
    if (!view) return;

    const pos = position ?? view.state.selection.main.head;
    view.dispatch({
      changes: { from: pos, to: pos, insert: text },
      selection: { anchor: pos + text.length },
    });
  }, []);

  const replaceSelection = useCallback((text: string) => {
    const view = editorRef.current?.view;
    if (!view) return;

    const selection = view.state.selection.main;
    view.dispatch({
      changes: { from: selection.from, to: selection.to, insert: text },
      selection: { anchor: selection.from + text.length },
    });
  }, []);

  const wrapSelection = useCallback((before: string, after: string) => {
    const view = editorRef.current?.view;
    if (!view) return;

    const selection = view.state.selection.main;
    const selectedText = view.state.sliceDoc(selection.from, selection.to);

    view.dispatch({
      changes: {
        from: selection.from,
        to: selection.to,
        insert: before + selectedText + after,
      },
      selection: {
        anchor: selection.from + before.length,
        head: selection.from + before.length + selectedText.length,
      },
    });
  }, []);

  const focus = useCallback(() => {
    editorRef.current?.view?.focus();
  }, []);

  return {
    editorRef,
    insertText,
    replaceSelection,
    wrapSelection,
    focus,
  };
}
